import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, MapPin, Menu, X, Lock as LockIcon } from "lucide-react";
import { BRAND } from "@/lib/brand";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/quote", label: "Instant Booking" },
  { to: "/request-quote", label: "Custom Quote" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  React.useEffect(() => { setOpen(false); }, [location]);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#e6e0d4]" data-testid="site-header">
      <div className="hidden md:flex items-center justify-between text-xs text-white bg-[var(--brand-teal)] px-6 py-2">
        <div className="flex items-center gap-5">
          <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{BRAND.phone}</span>
          <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Evansville, IN • Instant Booking within 20 miles</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[var(--brand-gold)] font-semibold tracking-wider uppercase">5-Star Rated • Licensed • Insured</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
          <img src={BRAND.logoUrl} alt="Top-Notch Pro Solutions" className="h-12 w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-extrabold text-[var(--brand-teal)] tracking-tight text-lg" style={{ fontFamily: "Outfit" }}>
              Top-Notch Pro Solutions
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-gold)] font-bold">
              Home Care & Property Improvement
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive
                    ? "text-[var(--brand-teal)] bg-[#e6e0d4]"
                    : "text-slate-700 hover:text-[var(--brand-teal)] hover:bg-[#f5f2ec]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/quote" className="btn-accent ml-3" data-testid="header-cta-quote" style={{ padding: "10px 20px" }}>
            Get Instant Booking
          </Link>
        </nav>

        <button
          className="md:hidden p-2 rounded-lg border border-[#dcd5c6]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#e6e0d4] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-semibold ${
                    isActive ? "bg-[#e6e0d4] text-[var(--brand-teal)]" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/quote" className="btn-accent mt-2 justify-center" data-testid="mobile-cta-quote">
              Get Instant Booking
            </Link>
            <a href={BRAND.phoneHref} className="text-center text-[var(--brand-teal)] font-semibold mt-2">{BRAND.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
}
